import { Toaster } from "sonner";
import { cn } from "@/lib/utils";

/**
 * BrandToast Component - Light Theme Editorial Design
 *
 * Clean toast notifications:
 * - Success: Light green with emerald text
 * - Error: Light red with red text
 * - Warning: Light amber with amber text
 * - Info: Light teal with teal text
 *
 * This file holds the provider component only. The imperative API
 * (`brandToast`, `transactionToast`) lives in `toast-api.tsx` and the glyphs in
 * `toast-icons.tsx`, so each file exports one kind of thing (Fast Refresh).
 */

/**
 * Toast Provider Component
 * Renders the toast container with brand styling
 */
export interface BrandToastProviderProps {
  /**
   * Toast position
   */
  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "top-center"
    | "bottom-center";

  /**
   * Maximum number of toasts to show
   */
  visibleToasts?: number;

  /**
   * Expand toasts by default
   */
  expand?: boolean;

  /**
   * Theme
   */
  theme?: "light" | "dark" | "system";
}

export function BrandToastProvider({
  position = "top-right",
  visibleToasts = 5,
  expand = false,
  theme = "light",
}: BrandToastProviderProps) {
  return (
    <Toaster
      position={position}
      visibleToasts={visibleToasts}
      expand={expand}
      theme={theme}
      closeButton
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: cn(
            // Base styles
            "w-full rounded-md border p-4 shadow-lg",
            "flex items-start gap-3",
            "animate-in slide-in-from-top-2 fade-in-0 duration-300",
            "data-[closed]:animate-out data-[closed]:slide-out-to-right-full data-[closed]:fade-out-0",
          ),
          title: "text-sm font-semibold",
          description: "text-sm opacity-90",
          actionButton: cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
            // The theme's own ink for this fill: `.dt-vnext` remaps
            // `--primary` to #83a9ff in dark, where white is 2.31:1.
            "bg-primary text-primary-foreground",
            "hover:bg-primary/90",
          ),
          cancelButton: cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
            "bg-secondary text-foreground",
            "hover:bg-secondary/80",
          ),
          closeButton: cn(
            "!absolute !right-2 !top-2",
            "!w-5 !h-5 rounded-full transition-all",
            "!bg-black/5 hover:!bg-black/10",
            "!text-foreground/60 hover:!text-foreground",
            "flex items-center justify-center",
            "!border-0 !p-0",
          ),
        },
      }}
    />
  );
}
